import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, ArrowLeft, Search, Users, Ticket, DollarSign } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { goBack } from '../../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EventosPublicosPage() {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchEventos();
  }, []);

  const fetchEventos = async () => {
    try {
      const res = await axios.get(`${API_URL}/eventos/publicos`);
      setEventos(res.data);
    } catch (err) {
      console.error('Error cargando eventos:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredEventos = eventos.filter(evento =>
    evento.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    evento.descripcion_corta?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    evento.lugar?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Ordenar eventos: próximos primero, pasados/cerrados al final
  const sortedEventos = [...filteredEventos].sort((a, b) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const fechaA = a.fecha_evento ? new Date(a.fecha_evento) : null;
    const fechaB = b.fecha_evento ? new Date(b.fecha_evento) : null;
    
    // Determinar si está "activo" (inscripciones abiertas Y fecha futura)
    const aActivo = a.inscripciones_abiertas !== false && fechaA && fechaA >= hoy;
    const bActivo = b.inscripciones_abiertas !== false && fechaB && fechaB >= hoy;
    
    // Si uno está activo y el otro no, el activo va primero
    if (aActivo && !bActivo) return -1;
    if (!aActivo && bActivo) return 1;
    
    // Si ambos están en el mismo grupo, ordenar por fecha
    if (fechaA && fechaB) {
      return fechaA - fechaB; // Más próximo primero
    }
    
    // Sin fecha al final
    if (fechaA && !fechaB) return -1;
    if (!fechaA && fechaB) return 1;
    
    return 0;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('T')[0].split('-');
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
            <button
              onClick={() => goBack()}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-100 rounded-full mb-4">
            <Calendar className="w-8 h-8 text-violet-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Eventos</h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Juras, encuentros, actividades culturales y más del Colegio Profesional
          </p>
        </div>

        {/* Search */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar eventos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        )}

        {/* No Events */}
        {!loading && sortedEventos.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No se encontraron eventos' : 'No hay eventos disponibles en este momento'}
            </p>
          </div>
        )}

        {/* Events Grid */}
        {!loading && sortedEventos.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedEventos.map((evento) => {
              const tieneListaEspera = evento.habilitar_lista_espera && (evento.cupo_agotado || evento.inscripciones_abiertas === false);
              const noDisponible = (evento.cupo_agotado || evento.inscripciones_abiertas === false) && !evento.habilitar_lista_espera;
              
              return (
              <Link
                key={evento.id}
                to={`/eventos/${evento.slug || evento.id}`}
                className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border border-gray-100 group ${noDisponible ? 'opacity-75' : ''}`}
                data-testid={`evento-card-${evento.id}`}
              >
                {/* Image */}
                <div className="aspect-video bg-gray-100 overflow-hidden relative">
                  {evento.imagen_portada ? (
                    <img
                      src={evento.imagen_portada}
                      alt={evento.nombre}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      style={{
                        objectPosition: evento.imagen_focal_point 
                          ? `${evento.imagen_focal_point.x}% ${evento.imagen_focal_point.y}%`
                          : 'center'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-50 to-purple-50">
                      <Calendar className="w-12 h-12 text-violet-300" />
                    </div>
                  )}
                  {/* Badges sobre la imagen */}
                  <div className="absolute top-2 right-2 flex flex-col gap-1">
                    {evento.cupo_agotado && (
                      evento.habilitar_lista_espera ? (
                        <Badge className="bg-orange-500 text-white text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          Lista de espera
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500 text-white text-xs">Cupo agotado</Badge>
                      )
                    )}
                    {!evento.cupo_agotado && evento.inscripciones_abiertas === false && (
                      evento.habilitar_lista_espera ? (
                        <Badge className="bg-orange-500 text-white text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          Lista de espera
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-500 text-white text-xs">Inscripciones cerradas</Badge>
                      )
                    )}
                    {evento.modalidad_admision === 'ticket_qr' && (
                      <Badge className="bg-violet-500 text-white text-xs">
                        <Ticket className="w-3 h-3 mr-1" />
                        Con entrada
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                    {evento.nombre}
                  </h3>
                  {evento.descripcion_corta && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {evento.descripcion_corta}
                    </p>
                  )}

                  {/* Meta */}
                  <div className="space-y-2 text-sm text-gray-500">
                    {evento.fecha_evento && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {formatDate(evento.fecha_evento)}
                          {evento.hora_evento && ` - ${evento.hora_evento} hs`}
                        </span>
                      </div>
                    )}
                    {evento.lugar && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{evento.lugar}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      {evento.cupo_maximo && (
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{evento.cupo_disponible} lugares</span>
                        </div>
                      )}
                      {evento.configuracion_pago?.tipo === 'gratis' ? (
                        <Badge variant="outline" className="text-green-600 border-green-200 text-xs">Gratis</Badge>
                      ) : evento.configuracion_pago?.precio > 0 && (
                        <div className="flex items-center gap-1 text-green-600">
                          <DollarSign className="w-4 h-4" />
                          <span>${evento.configuracion_pago.precio.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Button */}
                  <div className="mt-4 pt-4 border-t">
                    {(evento.cupo_agotado || evento.inscripciones_abiertas === false) ? (
                      evento.habilitar_lista_espera ? (
                        <Link 
                          to={`/eventos/${evento.slug || evento.id}/inscribirse`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-semibold text-sm"
                        >
                          <Clock className="w-4 h-4" />
                          Anotarme en lista de espera →
                        </Link>
                      ) : (
                        <span className="text-gray-400 font-semibold text-sm">
                          {evento.cupo_agotado ? 'Cupo agotado' : 'Inscripciones cerradas'}
                        </span>
                      )
                    ) : (
                      <span className="font-semibold text-sm text-violet-600 group-hover:text-violet-700">
                        Ver más →
                      </span>
                    )}
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>Colegio Profesional de Trabajadorxs Sociales - Distrito Lomas de Zamora</p>
        </div>
      </footer>
    </div>
  );
}
